/**
 * Contracts, and the review of AI-assisted extraction.
 *
 * ═══════════════════════════════════════════════════════════════════
 * AI-ASSISTED EXTRACTION — REVIEW REQUIRED.
 *
 * That phrase appears on this screen because it is literally true, and the
 * software behaves as though it is. Uploading a contract produces PROPOSALS:
 * each one shows what it read, how confident it is, and the passage it came
 * from. A reviewer accepts, amends or rejects each one.
 *
 * Until every proposal has been reviewed the contract stays "awaiting review",
 * it holds no approved terms, and it generates no reminders. Nothing an
 * extractor believed has any effect on anything until a person agreed with it.
 *
 * The reason is the notice deadline. Miss it on an auto-renewing contract and
 * the organisation is committed to another full term. A deadline calculated
 * from an unreviewed reading of a PDF is not a deadline anyone should rely on.
 *
 * The same claim has to survive the screen as well as the database, so an
 * unreviewed figure is never drawn to look like an approved one: on the
 * register and in the terms panel it is muted and captioned as unreviewed.
 * Otherwise the gate is real in the data and theatre on the projector.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms } from '../lib/tenant';
import { date, dateTime, humanise, money, statusLabel, statusTone, type BadgeTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

/**
 * The catalogue of terms the extractor knows about.
 *
 * The server sends this with every contract response, and it carries the TYPE
 * of each term. That is what lets one screen show a reviewer "£145,000.00" and
 * a date picker rather than a pence integer and an ISO string, without this
 * file keeping its own private opinion about what each field means.
 */
interface ContractField {
  key: string;
  label: string;
  help: string;
  type: 'date' | 'integer' | 'boolean' | 'money' | 'text';
}
type FieldType = ContractField['type'];

interface ContractSummary {
  id: string;
  reference: string;
  title: string;
  counterpartyName: string;
  contractType: string;
  category: string | null;
  status: string;
  commencementDate: string | null;
  expiryDate: string | null;
  noticeDeadline: string | null;
  noticePeriodDays: number | null;
  autoRenews: boolean;
  annualValueMinor: number;
  ownerName: string | null;
  daysToNotice: number | null;
  proposalsAwaitingReview: number;
  entityCode: string;
  entityName: string;
}

/**
 * Badge tone for a contract's own status.
 *
 * The shared map does not know the contract vocabulary, so an expired or
 * terminated contract would sit in the register in the same neutral grey as a
 * draft. Toned here rather than in lib/format because these words mean
 * something only on this screen.
 */
const CONTRACT_TONES: Record<string, BadgeTone> = {
  expiring: 'warn',
  expired: 'error',
  terminated: 'error',
  superseded: 'neutral',
};
function contractTone(status: string): BadgeTone {
  return CONTRACT_TONES[status] ?? statusTone(status);
}

/**
 * Badge tone for a reviewer's decision on one proposed term.
 *
 * Accepted and amended are settled outcomes and must read as settled. Left to
 * the shared status map both come back neutral — the same grey as an undecided
 * proposal — so an audit trail of who agreed to what looked as though nothing
 * had ever been decided.
 */
const DECISION_TONES: Record<string, BadgeTone> = {
  accepted: 'ok',
  amended: 'info',
  rejected: 'error',
  proposed: 'neutral',
};
function decisionTone(status: string): BadgeTone {
  return DECISION_TONES[status] ?? statusTone(status);
}

/* ------------------------------------------------------------------ *
 * Contract register
 * ------------------------------------------------------------------ */

export function ContractsPage() {
  const list = useAsync(
    () => api.get<{ contracts: ContractSummary[]; extractionNotice: string }>('/platform/contracts'),
    [],
  );

  return (
    <>
      <PageHeader
        title="Contracts"
        subtitle="Ordered by the notice deadline, because that is the date that costs money when it passes unnoticed."
      />

      <AsyncPanel state={list}>
        {(data) => {
          const awaiting = data.contracts.filter((c) => c.status === 'awaiting_review');
          const urgent = data.contracts.filter(
            (c) => c.daysToNotice !== null && c.daysToNotice >= 0 && c.daysToNotice <= 90,
          );
          // A contract whose terms nobody has accepted still has dates and a
          // value on it, because the extraction wrote them as proposals. They
          // are shown — a reviewer needs to see what is being proposed — but
          // never in the same ink as a figure a person has approved.
          const unreviewed = (c: ContractSummary) => c.status === 'awaiting_review';

          return (
            <div className="stack">
              {awaiting.length > 0 ? (
                <Alert tone="warn" title={`${awaiting.length} contract${awaiting.length === 1 ? '' : 's'} awaiting review`}>
                  <p>
                    Extracted terms have been proposed but not yet reviewed. These contracts hold no approved terms and
                    generate no reminders until somebody checks them. Their dates and values are shown greyed, because
                    nobody has agreed to them yet.
                  </p>
                </Alert>
              ) : null}

              {urgent.length > 0 ? (
                <Alert tone="error" title={`${urgent.length} notice deadline${urgent.length === 1 ? '' : 's'} within 90 days`}>
                  <ul className="smaller">
                    {urgent.map((c) => (
                      <li key={c.id}>
                        <Link to={`/contracts/${c.id}`}>{c.title}</Link> — notice by {date(c.noticeDeadline)}
                        {c.autoRenews ? ', or it renews automatically' : ''}
                        {unreviewed(c) ? ' (from terms not yet reviewed)' : ''}
                      </li>
                    ))}
                  </ul>
                </Alert>
              ) : null}

              <Card title="Contract register" flush>
                <DataTable
                  caption="Contracts"
                  rows={data.contracts}
                  rowKey={(c) => c.id}
                  emptyMessage="No contracts are recorded."
                  columns={[
                    {
                      key: 'ref',
                      header: 'Reference',
                      width: '9rem',
                      sortValue: (c) => c.reference,
                      render: (c) => (
                        <>
                          <Link to={`/contracts/${c.id}`}>
                            <span className="mono">{c.reference}</span>
                          </Link>
                          <div className="smaller muted">{c.entityCode}</div>
                        </>
                      ),
                    },
                    {
                      key: 'title',
                      header: 'Contract',
                      sortValue: (c) => c.title,
                      render: (c) => (
                        <>
                          <div className="truncate">{c.title}</div>
                          <div className="smaller muted">
                            {c.counterpartyName}
                            {c.category ? ` · ${c.category}` : ''}
                          </div>
                        </>
                      ),
                    },
                    {
                      key: 'value',
                      header: 'Annual value',
                      numeric: true,
                      sortValue: (c) => c.annualValueMinor,
                      render: (c) =>
                        unreviewed(c) ? (
                          <span className="muted">{money(c.annualValueMinor, { blankIfZero: true })}</span>
                        ) : (
                          <Money minor={c.annualValueMinor} blankIfZero />
                        ),
                    },
                    {
                      key: 'expiry',
                      header: 'Expires',
                      sortValue: (c) => c.expiryDate,
                      render: (c) =>
                        unreviewed(c) ? <span className="muted">{date(c.expiryDate)}</span> : date(c.expiryDate),
                    },
                    {
                      key: 'notice',
                      header: 'Notice by',
                      sortValue: (c) => c.noticeDeadline,
                      render: (c) =>
                        c.noticeDeadline ? (
                          <>
                            <div
                              className={
                                unreviewed(c)
                                  ? 'muted'
                                  : c.daysToNotice !== null && c.daysToNotice <= 90
                                    ? 'num--negative'
                                    : undefined
                              }
                            >
                              {date(c.noticeDeadline)}
                            </div>
                            <div className="smaller muted">
                              {/*
                                A countdown is the most dangerous thing on this
                                row: it invites the reader to diarise a date
                                that came out of an unreviewed reading of a PDF.
                                So an unreviewed contract says where the date
                                came from instead of how long is left.
                              */}
                              {unreviewed(c)
                                ? 'from terms not yet reviewed'
                                : c.daysToNotice === null
                                  ? ''
                                  : c.daysToNotice < 0
                                    ? 'passed'
                                    : `${c.daysToNotice} days`}
                            </div>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        ),
                    },
                    {
                      key: 'renew',
                      header: 'Renewal',
                      render: (c) =>
                        c.autoRenews ? <Badge tone="warn">Automatic</Badge> : <Badge tone="neutral">Expires</Badge>,
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      sortValue: (c) => c.status,
                      render: (c) => (
                        <>
                          <Badge tone={contractTone(c.status)}>{statusLabel(c.status)}</Badge>
                          {c.proposalsAwaitingReview > 0 ? (
                            <div className="smaller">
                              <Badge tone="warn">{c.proposalsAwaitingReview} to review</Badge>
                            </div>
                          ) : null}
                        </>
                      ),
                    },
                    { key: 'owner', header: 'Owner', render: (c) => <span className="smaller">{c.ownerName ?? '—'}</span> },
                  ]}
                />
              </Card>

              <p className="smaller muted">
                <strong>{data.extractionNotice}.</strong> Nothing extracted takes effect until a person accepts it.
              </p>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Contract detail and the review gate
 * ------------------------------------------------------------------ */

interface Extraction {
  id: string;
  field_key: string;
  field_label: string;
  extracted_value: string | null;
  normalised_value: string | null;
  confidence: number;
  evidence_text: string | null;
  evidence_location: string | null;
  provider: string;
  model: string | null;
  status: string;
  applied_value: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_note: string | null;
}

interface ContractDetailResponse {
  contract: Record<string, unknown> & {
    id: string;
    reference: string;
    title: string;
    counterparty_name: string;
    counterparty_type: string;
    supplier_id: string | null;
    supplier_code: string | null;
    status: string;
    contract_type: string;
    category: string | null;
    commencement_date: string | null;
    expiry_date: string | null;
    notice_period_days: number | null;
    auto_renews: boolean;
    renewal_term_months: number | null;
    indexation_basis: string | null;
    indexation_cap_percent: string | null;
    // BIGINT: a real Postgres connection hands this over as a string to avoid
    // precision loss, the embedded driver as a number. Both are read the same.
    annual_value_minor: number | string | null;
    payment_terms_days: number | null;
    termination_provisions: string | null;
    owner_name: string | null;
    approved_by_name: string | null;
    terms_approved_at: string | null;
    entity_code: string;
    entity_name: string;
  };
  extractions: Extraction[];
  reminders: Array<{ id: string; reminder_type: string; due_date: string; title: string; body: string; status: string; assigned_to_name: string | null }>;
  fields: ContractField[];
  extractionNotice: string;
  canApproveTerms: boolean;
}

function confidenceTone(confidence: number): 'ok' | 'warn' | 'error' {
  if (confidence >= 85) return 'ok';
  if (confidence >= 70) return 'warn';
  return 'error';
}

/**
 * What actually read the document.
 *
 * The screen says "AI-assisted extraction" and puts a confidence figure beside
 * every term, so the first question from anyone responsible for information
 * security is where the contract text went. Here the honest answer is the good
 * one — nowhere, it is a local pattern matcher — and it is worth nothing at all
 * unless the screen says so out loud.
 */
function providerName(provider: string | null | undefined): string {
  if (provider === 'local_deterministic') return 'the built-in pattern extractor';
  if (!provider) return 'an extractor that was not recorded';
  return humanise(provider);
}

/** The same name at the head of a sentence. */
function providerSubject(provider: string | null | undefined): string {
  if (provider === 'local_deterministic') return 'The built-in pattern extractor';
  if (!provider) return 'The extractor';
  return humanise(provider);
}

function providerNote(provider: string | null | undefined): string {
  if (provider === 'local_deterministic') {
    return 'Read by the built-in pattern extractor: it matches documented wording in the text supplied to it, runs inside this deployment, and sends the contract text to no external service.';
  }
  if (!provider) return 'The extractor that read these terms was not recorded.';
  return `Read by ${humanise(provider)}.`;
}

/**
 * What shape a term is.
 *
 * Asked of the catalogue the server publishes rather than guessed from the key.
 * The fallback exists only so that a term the catalogue has not caught up with
 * still renders as something a person can read, instead of showing a reviewer a
 * pence integer.
 */
function fieldTypeOf(fields: ContractField[], key: string): FieldType {
  const declared = fields.find((f) => f.key === key);
  if (declared) return declared.type;
  if (key.endsWith('_minor')) return 'money';
  if (key.endsWith('_date')) return 'date';
  if (key.endsWith('_days') || key.endsWith('_months')) return 'integer';
  if (key === 'auto_renews') return 'boolean';
  return 'text';
}

function fieldHelpOf(fields: ContractField[], key: string): string | undefined {
  return fields.find((f) => f.key === key)?.help;
}

/**
 * Show a proposed value the way a person reads it.
 *
 * The extractor normalises money to pence and dates to ISO so the value can be
 * written straight onto the record. A reviewer checking "£145,000.00" against
 * the contract in front of them should not have to decode "14500000" first.
 */
function readableTerm(type: FieldType, fieldKey: string, value: string | null): string {
  if (value === null || value === '') return '—';
  if (fieldKey === 'indexation_cap_percent' && !value.includes('%')) return `${value}%`;
  switch (type) {
    case 'money': {
      const pence = Number(value);
      return Number.isFinite(pence) ? money(pence) : value;
    }
    case 'date':
      return date(value);
    case 'integer':
      return fieldKey.endsWith('_months') ? `${value} months` : `${value} days`;
    case 'boolean':
      if (fieldKey === 'auto_renews') return value === 'true' ? 'Yes — it renews automatically' : 'No — it expires';
      return value === 'true' ? 'Yes' : 'No';
    default:
      return value;
  }
}

/**
 * Monospace is for values with a machine shape — an amount, a date, a count.
 * A contract clause is a sentence: set in monospace inside a table cell it
 * wraps across three lines and reads like console output.
 */
function isTabular(type: FieldType): boolean {
  return type === 'money' || type === 'date' || type === 'integer';
}

/**
 * Turn the stored value into something a reviewer can type over.
 *
 * Money is held in pence and dates in ISO so they can go straight onto the
 * record. Putting 14500000 in the box and asking a Finance Director for pence
 * is not a review: they cannot check it against the agreement in front of them
 * without doing the arithmetic first, which is exactly the step the review
 * exists to avoid.
 */
function amendDraft(type: FieldType, value: string | null): string {
  if (type === 'boolean') return value === 'true' ? 'true' : 'false';
  if (value === null) return '';
  if (type === 'money') {
    const pence = Number(value);
    return Number.isFinite(pence) ? (pence / 100).toFixed(2) : value;
  }
  if (type === 'date') return value.slice(0, 10);
  return value;
}

/** And back again, into exactly the form the server writes onto the contract. */
function amendPayload(type: FieldType, draft: string): string {
  if (type === 'money') return String(Math.round(Number(draft) * 100));
  return draft.trim();
}

function amendIsValid(type: FieldType, draft: string): boolean {
  const trimmed = draft.trim();
  if (trimmed.length === 0) return false;
  if (type === 'money') return Number.isFinite(Number(trimmed));
  if (type === 'integer') return Number.isInteger(Number(trimmed));
  if (type === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  return true;
}

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useSession();
  const t = useTerms();
  const detail = useAsync(() => api.get<ContractDetailResponse>(`/platform/contracts/${id}`), [id]);

  const [extracting, setExtracting] = useState(false);
  const [text, setText] = useState('');
  const [amending, setAmending] = useState<Extraction | null>(null);
  const [amendValue, setAmendValue] = useState('');
  const [amendNote, setAmendNote] = useState('');
  // What the last action actually did. Approving a contract removes its own
  // button and empties the proposals list, so without this the most important
  // click on the screen produces no visible outcome at all.
  const [outcome, setOutcome] = useState<{ title: string; body: string } | null>(null);

  const extract = useMutation(async (contractText: string) =>
    api.post<{ proposed: number; notice: string }>(`/platform/contracts/${id}/extract`, { text: contractText }),
  );
  const review = useMutation(
    async (extractionId: string, decision: 'accept' | 'amend' | 'reject', value?: string, note?: string) =>
      api.post<{ contractId: string; fieldKey: string; appliedValue: string | null }>(
        `/platform/contracts/extractions/${extractionId}/review`,
        { decision, value, note },
      ),
  );
  const approveTerms = useMutation(async () =>
    api.post<{ reference: string; remindersCreated: number; outstandingProposals: number }>(
      `/platform/contracts/${id}/approve-terms`,
      {},
    ),
  );

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const c = data.contract;
        const proposed = data.extractions.filter((e) => e.status === 'proposed');
        const reviewed = data.extractions.filter((e) => e.status !== 'proposed');
        const termsApproved = c.terms_approved_at !== null;
        const mayReview = can('contract.approve_terms');

        // The terms still under proposal, so the panel of recorded terms can
        // mark exactly those figures rather than greying the whole card.
        const awaitingKeys = new Set(proposed.map((e) => e.field_key));

        // One line naming what read this contract. Every seeded extraction is
        // local, but a contract could carry terms from two extractors, and then
        // no single sentence is true of all of them.
        const providers = [...new Set(data.extractions.map((e) => e.provider))];
        const singleProvider = providers.length === 1 ? (providers[0] ?? null) : null;
        const extractorNote =
          data.extractions.length === 0
            ? null
            : singleProvider
              ? providerNote(singleProvider)
              : 'These terms were read by more than one extractor. Each proposal says which read it.';

        const amendType = amending ? fieldTypeOf(data.fields, amending.field_key) : 'text';
        const amendHelp = amending ? fieldHelpOf(data.fields, amending.field_key) : undefined;
        const amendUnit = amending?.field_key.endsWith('_months') ? 'months' : 'days';

        // The approval gate, and the two reasons the button must not be drawn:
        // the server refuses while any proposal is outstanding, and it refuses
        // a contract with no expiry date because there is no deadline to
        // calculate. A button that can only fail is worse than no button.
        const approvalStage = c.status === 'awaiting_review' || c.status === 'draft';
        const mayApproveNow = data.canApproveTerms && proposed.length === 0 && approvalStage;

        const counterparty =
          c.counterparty_type === 'supplier' && c.supplier_id && can('supplier.view') ? (
            <Link to={`/suppliers/${c.supplier_id}`}>
              {c.supplier_code ? `${c.supplier_code} — ` : ''}
              {c.counterparty_name}
            </Link>
          ) : (
            c.counterparty_name
          );

        /**
         * One row of the recorded-terms panel.
         *
         * A term still awaiting review is shown — the reviewer has to see what
         * is being proposed — but it is muted and captioned, so an approved
         * figure and a machine's guess never look the same.
         */
        const termRow = (fieldKey: string | null, label: ReactNode, value: ReactNode): [ReactNode, ReactNode] =>
          fieldKey !== null && awaitingKeys.has(fieldKey)
            ? [
                label,
                <>
                  <span className="muted">{value}</span>
                  {/* Short on purpose: this column is narrow and the marker
                      appears on every unreviewed term. The card title and
                      subtitle carry the full explanation. */}
                  <div className="smaller muted">Awaiting review</div>
                </>,
              ]
            : [label, value];

        return (
          <>
            <PageHeader
              title={c.title}
              subtitle={
                <>
                  <span className="mono">{c.reference}</span> · {counterparty} · {c.entity_name}
                </>
              }
              meta={
                <>
                  <Badge tone={contractTone(c.status)}>{statusLabel(c.status)}</Badge>
                  {c.auto_renews ? <Badge tone="warn">Renews automatically</Badge> : null}
                  {proposed.length > 0 ? <Badge tone="warn">{proposed.length} terms awaiting review</Badge> : null}
                </>
              }
              actions={
                <div className="btn-group">
                  {can('contract.manage') ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setOutcome(null);
                        setExtracting(true);
                      }}
                    >
                      Run extraction
                    </button>
                  ) : null}
                  {mayApproveNow && c.expiry_date ? (
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={approveTerms.busy}
                      onClick={() => {
                        setOutcome(null);
                        void approveTerms.run().then((r) => {
                          if (r) {
                            setOutcome({
                              title: 'Terms approved',
                              body: `${r.reference} is approved and now holds terms a person has accepted. ${
                                r.remindersCreated === 1
                                  ? '1 reminder has'
                                  : `${r.remindersCreated} reminders have`
                              } been generated from them.`,
                            });
                            detail.reload();
                          }
                        });
                      }}
                    >
                      {approveTerms.busy ? 'Approving…' : 'Approve the terms'}
                    </button>
                  ) : null}
                </div>
              }
            />

            <ErrorNotice error={approveTerms.error} />

            {outcome ? (
              <Alert tone="ok" title={outcome.title}>
                <p>{outcome.body}</p>
              </Alert>
            ) : null}

            {mayApproveNow && !c.expiry_date ? (
              <Alert tone="warn" title="The terms cannot be approved yet">
                <p>
                  No expiry date is recorded, so no notice deadline can be calculated and no reminder could be
                  generated. Record the expiry date before approving the contract.
                </p>
              </Alert>
            ) : null}

            {proposed.length > 0 ? (
              <Alert tone="warn" title="AI-assisted extraction — review required">
                <p>
                  {proposed.length} proposed term{proposed.length === 1 ? '' : 's'} {proposed.length === 1 ? 'has' : 'have'} not
                  been reviewed. None of them is part of the contract record, and no reminder has been generated from
                  any of them. Accept, amend or reject each one, then approve the contract.
                </p>
              </Alert>
            ) : null}

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                {proposed.length > 0 ? (
                  <Card
                    title="Proposed terms"
                    subtitle="Least confident first — those are the ones most likely to be wrong."
                  >
                    <div className="stack">
                      {extractorNote ? <p className="smaller muted">{extractorNote}</p> : null}

                      {!mayReview ? (
                        <Alert tone="info" title="You can see these proposals but not act on them">
                          <p className="smaller">
                            Accepting, amending or rejecting a contract term needs the contract term approval
                            permission. The proposals and their evidence are shown here so that you can check them.
                          </p>
                        </Alert>
                      ) : null}

                      {proposed.map((e) => {
                        const type = fieldTypeOf(data.fields, e.field_key);
                        return (
                          <article key={e.id} className="extraction">
                            <div className="row row--between">
                              <div>
                                <p className="strong">{e.field_label}</p>
                                <p className={isTabular(type) ? 'mono' : undefined}>
                                  {readableTerm(type, e.field_key, e.normalised_value)}
                                </p>
                                {e.extracted_value && e.extracted_value !== e.normalised_value ? (
                                  <p className="smaller muted">read as “{e.extracted_value}”</p>
                                ) : null}
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <Badge tone={confidenceTone(e.confidence)}>{e.confidence}% confident</Badge>
                                <div className="smaller muted">by {providerName(e.provider)}</div>
                              </div>
                            </div>

                            {e.evidence_text ? (
                              <blockquote className="extraction__evidence">
                                “{e.evidence_text}”
                                {e.evidence_location ? (
                                  <footer className="smaller muted">{e.evidence_location}</footer>
                                ) : null}
                              </blockquote>
                            ) : null}

                            {mayReview ? (
                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn--sm btn--primary"
                                  disabled={review.busy}
                                  onClick={() => {
                                    setOutcome(null);
                                    void review.run(e.id, 'accept').then((r) => {
                                      if (r) detail.reload();
                                    });
                                  }}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--sm"
                                  onClick={() => {
                                    setOutcome(null);
                                    setAmending(e);
                                    setAmendValue(amendDraft(type, e.normalised_value));
                                    setAmendNote('');
                                  }}
                                >
                                  Amend
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--sm btn--danger"
                                  disabled={review.busy}
                                  onClick={() => {
                                    setOutcome(null);
                                    void review.run(e.id, 'reject').then((r) => {
                                      if (r) detail.reload();
                                    });
                                  }}
                                >
                                  Reject
                                </button>
                              </div>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                    <ErrorNotice error={review.error} />
                  </Card>
                ) : null}

                {reviewed.length > 0 ? (
                  <Card
                    title="Reviewed terms"
                    subtitle="Who decided, what they decided, and what the extractor had proposed."
                    flush
                  >
                    <DataTable
                      caption="Terms that have been reviewed"
                      rows={reviewed}
                      rowKey={(e) => e.id}
                      columns={[
                        { key: 'field', header: 'Term', render: (e) => e.field_label },
                        {
                          key: 'proposed',
                          header: 'Extractor proposed',
                          render: (e) => {
                            const type = fieldTypeOf(data.fields, e.field_key);
                            return (
                              <>
                                <span className={isTabular(type) ? 'mono smaller' : 'smaller'}>
                                  {readableTerm(type, e.field_key, e.normalised_value)}
                                </span>
                                <div className="smaller muted">{e.confidence}% confident</div>
                              </>
                            );
                          },
                        },
                        {
                          key: 'applied',
                          header: 'In force',
                          render: (e) => {
                            const type = fieldTypeOf(data.fields, e.field_key);
                            return e.applied_value ? (
                              <span className={isTabular(type) ? 'mono smaller' : 'smaller'}>
                                {readableTerm(type, e.field_key, e.applied_value)}
                              </span>
                            ) : (
                              <span className="muted smaller">Rejected — not used</span>
                            );
                          },
                        },
                        {
                          key: 'status',
                          header: 'Decision',
                          render: (e) => <Badge tone={decisionTone(e.status)}>{statusLabel(e.status)}</Badge>,
                        },
                        {
                          key: 'by',
                          header: 'Reviewed by',
                          render: (e) => (
                            <span className="smaller">
                              {e.reviewed_by_name ?? '—'}
                              {e.reviewed_at ? <div className="muted">{dateTime(e.reviewed_at)}</div> : null}
                              {e.review_note ? <div className="muted">“{e.review_note}”</div> : null}
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Card>
                ) : null}

                <Card title="Reminders" subtitle="Generated only from terms a person has approved." flush>
                  {data.reminders.length === 0 ? (
                    <div style={{ padding: '1rem' }}>
                      <EmptyState title="No reminders">
                        {c.status === 'awaiting_review'
                          ? 'This contract has not been reviewed, so no deadline has been calculated from it.'
                          : 'No reminders are recorded for this contract.'}
                      </EmptyState>
                    </div>
                  ) : (
                    <DataTable
                      caption="Contract reminders"
                      rows={data.reminders}
                      rowKey={(r) => r.id}
                      columns={[
                        { key: 'type', header: 'Type', render: (r) => <Badge tone={r.reminder_type === 'notice_deadline' ? 'warn' : 'neutral'}>{statusLabel(r.reminder_type)}</Badge> },
                        { key: 'due', header: 'Due', render: (r) => date(r.due_date) },
                        { key: 'title', header: 'Reminder', render: (r) => <><div>{r.title}</div><div className="smaller muted">{r.body}</div></> },
                        { key: 'to', header: 'Assigned to', render: (r) => <span className="smaller">{r.assigned_to_name ?? '—'}</span> },
                        { key: 'status', header: 'Status', render: (r) => <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge> },
                      ]}
                    />
                  )}
                </Card>
              </div>

              <div className="stack">
                <Card
                  title={termsApproved ? 'Terms in force' : 'Recorded terms — not yet approved'}
                  subtitle={
                    termsApproved
                      ? 'Only what a person has approved.'
                      : 'What the extraction has proposed. Nothing here is in force and no reminder has been generated from it.'
                  }
                >
                  <DefinitionList
                    items={[
                      [c.counterparty_type === 'supplier' ? t.supplier : 'Counterparty', counterparty],
                      ['Type', statusLabel(c.contract_type)],
                      termRow('commencement_date', 'Commences', date(c.commencement_date)),
                      termRow('expiry_date', 'Expires', date(c.expiry_date)),
                      termRow(
                        'notice_period_days',
                        'Notice period',
                        c.notice_period_days ? `${c.notice_period_days} days` : '—',
                      ),
                      termRow('auto_renews', 'Renews automatically', c.auto_renews ? 'Yes' : 'No'),
                      termRow(
                        'renewal_term_months',
                        'Renewal term',
                        c.renewal_term_months ? `${c.renewal_term_months} months` : '—',
                      ),
                      termRow(
                        'annual_value_minor',
                        'Annual value',
                        c.annual_value_minor ? money(Number(c.annual_value_minor)) : '—',
                      ),
                      termRow(
                        'payment_terms_days',
                        'Payment terms',
                        c.payment_terms_days ? `${c.payment_terms_days} days` : '—',
                      ),
                      termRow('indexation_basis', 'Indexation', c.indexation_basis ?? '—'),
                      ['Owner', c.owner_name ?? '—'],
                      [
                        'Terms approved',
                        c.approved_by_name ? (
                          <>
                            {c.approved_by_name}
                            <div className="smaller muted">{c.terms_approved_at ? dateTime(c.terms_approved_at) : ''}</div>
                          </>
                        ) : (
                          'Not yet approved'
                        ),
                      ],
                    ]}
                  />
                  {c.termination_provisions ? (
                    <>
                      <p className="field__label" style={{ marginTop: '1rem' }}>
                        Termination
                      </p>
                      <p className="smaller muted">{c.termination_provisions}</p>
                    </>
                  ) : null}
                </Card>
              </div>
            </div>

            <Modal
              open={extracting}
              onClose={() => setExtracting(false)}
              wide
              title="Extract terms from the contract"
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setExtracting(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={extract.busy || text.trim().length < 40}
                    onClick={() =>
                      void extract.run(text).then((r) => {
                        if (r) {
                          setExtracting(false);
                          setText('');
                          setOutcome({
                            title: r.proposed === 0 ? 'Nothing was recognised' : 'Extraction complete',
                            body:
                              r.proposed === 0
                                ? 'The extractor found no terms it recognised in that text. Nothing has been changed on the contract.'
                                : `${r.proposed} term${r.proposed === 1 ? '' : 's'} proposed for review. None of them is part of the contract record, and no reminder has been generated, until you accept or amend it.`,
                          });
                          detail.reload();
                        }
                      })
                    }
                  >
                    {extract.busy ? 'Reading…' : 'Extract terms'}
                  </button>
                </>
              }
            >
              <div className="stack">
                <Alert tone="info" title="What this does, and what it does not do">
                  <p className="smaller">
                    The extractor reads the text and proposes the terms it believes it has found, each with a confidence
                    figure and the passage it came from. It changes nothing. Every proposal has to be accepted, amended
                    or rejected by a person before it becomes part of the contract record or produces a reminder.
                  </p>
                  {extractorNote ? <p className="smaller">{extractorNote}</p> : null}
                </Alert>
                <TextArea
                  label="Contract text"
                  hint="Paste the text of the agreement — at least 40 characters, or there is nothing to read. In a full deployment this comes from the uploaded document."
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                  rows={14}
                  required
                />
                <ErrorNotice error={extract.error} />
              </div>
            </Modal>

            <Modal
              open={amending !== null}
              onClose={() => setAmending(null)}
              title={`Amend ${amending?.field_label ?? ''}`}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setAmending(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={review.busy || amending === null || !amendIsValid(amendType, amendValue)}
                    onClick={() => {
                      if (!amending) return;
                      void review
                        .run(amending.id, 'amend', amendPayload(amendType, amendValue), amendNote || undefined)
                        .then((r) => {
                          if (r) {
                            setAmending(null);
                            detail.reload();
                          }
                        });
                    }}
                  >
                    {review.busy ? 'Recording…' : 'Save the correction'}
                  </button>
                </>
              }
            >
              {amending ? (
                <div className="stack">
                  <p className="smaller muted">
                    {providerSubject(amending.provider)} proposed{' '}
                    <span className={isTabular(amendType) ? 'mono' : undefined}>
                      {readableTerm(amendType, amending.field_key, amending.normalised_value)}
                    </span>{' '}
                    at {amending.confidence}% confidence. Your value replaces it.
                  </p>

                  {/*
                    The box is typed to the term, so a reviewer enters pounds and
                    a calendar date rather than pence and an ISO string. The
                    conversion back into what the record stores happens on the
                    way out, in amendPayload, where it can be seen.
                  */}
                  {amendType === 'boolean' ? (
                    <Select
                      label="Correct value"
                      hint={amendHelp}
                      value={amendValue}
                      onChange={(e) => setAmendValue(e.currentTarget.value)}
                      required
                    >
                      <option value="true">{readableTerm('boolean', amending.field_key, 'true')}</option>
                      <option value="false">{readableTerm('boolean', amending.field_key, 'false')}</option>
                    </Select>
                  ) : amendType === 'date' ? (
                    <TextInput
                      label="Correct date"
                      type="date"
                      hint={amendHelp}
                      value={amendValue}
                      onChange={(e) => setAmendValue(e.currentTarget.value)}
                      required
                    />
                  ) : amendType === 'money' ? (
                    <TextInput
                      label="Correct value (£)"
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      hint={amendHelp ? `${amendHelp} In pounds and pence.` : 'In pounds and pence.'}
                      value={amendValue}
                      onChange={(e) => setAmendValue(e.currentTarget.value)}
                      required
                    />
                  ) : amendType === 'integer' ? (
                    <TextInput
                      label={`Correct value (${amendUnit})`}
                      type="number"
                      step="1"
                      min="0"
                      hint={amendHelp}
                      value={amendValue}
                      onChange={(e) => setAmendValue(e.currentTarget.value)}
                      required
                    />
                  ) : (
                    <TextArea
                      label="Correct value"
                      hint={amendHelp}
                      rows={3}
                      value={amendValue}
                      onChange={(e) => setAmendValue(e.currentTarget.value)}
                      required
                    />
                  )}

                  <TextArea
                    label="Note (optional)"
                    hint="Why the proposal was wrong. Useful evidence, and useful feedback."
                    value={amendNote}
                    onChange={(e) => setAmendNote(e.currentTarget.value)}
                  />
                  <ErrorNotice error={review.error} />
                </div>
              ) : null}
            </Modal>
          </>
        );
      }}
    </AsyncPanel>
  );
}
